import {
	BasePickerCompat as BasePicker,
	CheckboxCompat as Checkbox,
	CommandButtonCompat as CommandButton,
	IBasePicker,
	IBasePickerProps,
	ICheckboxStyles,
	IconButtonCompat as IconButton,
	ITag,
	SeparatorCompat as Separator,
	StackCompat as Stack,
	TagPickerCompat as TagPicker,
} from 'components/FluentUICompat';
import {useBoolean} from '@fluentui/react-hooks';
import React, {ChangeEvent, useEffect, useMemo, useRef, useState} from 'react';
import {v4 as uuidv4} from 'uuid';
import {
	LocalizedDatePicker,
	LocalizedDatePickerProps,
} from 'components/hookForms';
import {useTranslation} from 'react-i18next';
import {formatDateTime} from 'i18n/localeDateFormat';
import {EntityListColumn, EntityListFilter} from './EntityList';
import {FilterOption} from 'types';
import {DEFAULT_DATE} from './EntityUtils';
import {
	isAfter,
	isBefore,
	isWithinInterval,
	max,
	min,
	parseISO,
} from 'date-fns';
import {IRefObject} from '@fluentui/react';

type FilterItemProps = {
	col: EntityListColumn;
	selectedFilters: EntityListFilter[];
	setSelectedFilters: (selectedFilters: EntityListFilter[]) => void;
	sortedValues: FilterOption[];
	getFilterFn: (match: any) => (item: any) => any;
	getBlankFilterFn: (item: any) => boolean;
	blanksExists: boolean;
	hideHeader?: boolean;
	expandedByDefault?: boolean;
};

type FilterItemDateProps = {
	col: EntityListColumn;
	selectedFilters: EntityListFilter[];
	setSelectedFilters: (selectedFilters: EntityListFilter[]) => void;
	colValues: any[];
};

type DateRange = {
	dateFrom: Date | undefined;
	dateTo: Date | undefined;
};

const MIN_CHECKBOX_NUM = 15;
const DEFAULT_CHECKBOX_NUM = 25;
const DEFAULT_CHECKBOX_DELTA = 100;
const DEFAULT_CHECKBOX_DELTA_2 = 500;
const DEFAULT_SUGGESTION_NUM = 100;

export const FilterItem: React.FC<FilterItemProps> = props => {
	const {t} = useTranslation('components/entitylist', {
		keyPrefix: 'FilterComponent',
	});

	const {
		col,
		selectedFilters,
		setSelectedFilters,
		sortedValues,
		getFilterFn,
		getBlankFilterFn,
		blanksExists,
		expandedByDefault,
		hideHeader,
	} = props;

	const {name: columnName, key: columnKey, filterOnFilter} = col;

	const [isExpanded, {toggle: toggleExpanded}] = useBoolean(
		expandedByDefault === true,
	);

	const [limitCheckboxes, setLimitChechboxes] = useState(DEFAULT_CHECKBOX_NUM);

	const [searchedOptions, setSearchedOptions] = useState<FilterOption[]>([]);

	const refTagPicker = useRef<IBasePicker<ITag>>(null);

	const selectedOptionKeys = useMemo(
		() =>
			selectedFilters
				.filter(x => x.columnKey === columnKey)
				.map(x => x.selection),
		[selectedFilters],
	);

	const checkboxBlank = useMemo(
		() =>
			blanksExists && (
				<Checkbox
					key={uuidv4()}
					label={`(${t('Blank')})`}
					defaultChecked={selectedOptionKeys.includes('')}
					onChange={(_, checked) => {
						const selectedFiltersNew = selectedFilters.filter(
							x => !(x.columnKey === columnKey && x.selection === ''),
						);
						if (checked) {
							const selectedFilter: EntityListFilter = {
								columnKey,
								selection: '',
								display: `(${t('Blank')})`,
								filterFn: getBlankFilterFn,
							};

							setSelectedFilters([...selectedFiltersNew, selectedFilter]);
						} else {
							setSelectedFilters(selectedFiltersNew);
						}
					}}
				/>
			),
		[selectedFilters],
	);

	const checkboxes = useMemo(() => {
		const shownOptions = sortedValues
			.filter(
				x =>
					!(
						x.name === '' ||
						searchedOptions.find(y => y.optionKey === x.optionKey)
					),
			)
			.slice(0, limitCheckboxes);

		const hiddenSelectedOptionsIds = selectedOptionKeys.filter(
			x =>
				!(
					shownOptions.filter(y => y.optionKey === x).length > 0 ||
					searchedOptions.filter(y => y.optionKey === x).length > 0
				),
		);

		const hiddenSelectedOptions = sortedValues.filter(x =>
			hiddenSelectedOptionsIds.includes(x.optionKey),
		);

		const checkboxOptions = [
			...searchedOptions,
			...hiddenSelectedOptions,
			...shownOptions,
		];
		return checkboxOptions.map((val, j) => (
			<Checkbox
				key={uuidv4()}
				label={val.name}
				defaultChecked={selectedOptionKeys.includes(val.optionKey)}
				onChange={(_, checked) => {
					const selectedFiltersNew = selectedFilters.filter(
						x => !(x.columnKey === columnKey && x.selection === val.optionKey),
					);

					if (checked) {
						const selectedFilter: EntityListFilter = {
							columnKey,
							selection: val.optionKey,
							display: val.name,
							filterFn: getFilterFn(val.optionKey),
						};
						setSelectedFilters([...selectedFiltersNew, selectedFilter]);
					} else {
						setSelectedFilters(selectedFiltersNew);
					}
				}}
			/>
		));
	}, [sortedValues, selectedFilters, limitCheckboxes, searchedOptions]);

	const numHiddenOptions = sortedValues.length - checkboxes.length;

	const onResolveSuggestions = (
		filterText: string,
		selectedItems?: ITag[],
	): ITag[] => {
		if (!filterText) {
			return [];
		}

		const safeFilterText = filterText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

		const reFilter = new RegExp((safeFilterText ?? '').toLowerCase());

		const fieldValueSuggestions = sortedValues.filter(
			x =>
				reFilter.test(String(x.name).toLowerCase()) &&
				!selectedOptionKeys.includes(x.optionKey),
		);

		const suggestions: ITag[] = (fieldValueSuggestions ?? [])
			.slice(0, DEFAULT_SUGGESTION_NUM)
			.map((val, j) => ({
				key: val.optionKey,
				name: val.name,
			}));

		return suggestions;
	};

	const onEmptyResolveSuggestions = () => {
		const fieldValueSuggestions = sortedValues;

		const suggestions: ITag[] = (fieldValueSuggestions ?? [])
			.slice(0, 100)
			.map((val, j) => ({
				key: val.optionKey,
				name: val.name,
			}));

		return suggestions;
	};

	const onItemSelected = (selectedItem?: ITag) => {
		if (selectedItem) {
			const selectedFiltersNew = selectedFilters.filter(
				x => !(x.columnKey === columnKey && x.selection === selectedItem.key),
			);

			const selectedFilter: EntityListFilter = {
				columnKey,
				selection: selectedItem.key as string,
				display: selectedItem.name,
				filterFn: getFilterFn(selectedItem.key as string),
			};

			setSelectedFilters([...selectedFiltersNew, selectedFilter]);

			setSearchedOptions(prev => [
				...prev,
				{
					optionKey: selectedItem.key as string,
					name: selectedItem.name,
				},
			]);
		}

		return null;
	};

	return (
		<>
			<Stack tokens={{childrenGap: 8}}>
				{!hideHeader && (
					<CommandButton
						iconProps={{iconName: isExpanded ? 'ChevronUp' : 'ChevronDown'}}
						text={columnName}
						onClick={() => toggleExpanded()}
						ariaLabel={`Toggle filter ${columnName}`}
					/>
				)}

				<Stack.Item
					styles={{
						root: {
							display: isExpanded ? 'block' : 'none',
						},
					}}
				>
					<>
						{filterOnFilter && sortedValues.length > MIN_CHECKBOX_NUM && (
							<>
								<TagPicker
									onResolveSuggestions={onResolveSuggestions}
									onEmptyResolveSuggestions={onEmptyResolveSuggestions}
									onItemSelected={onItemSelected}
									pickerSuggestionsProps={{
										suggestionsHeaderText: columnName,
										noResultsFoundText: t('NoResult'),
									}}
									inputProps={{
										placeholder: t('Search'),
									}}
									componentRef={
										refTagPicker as unknown as IRefObject<IBasePicker<ITag>>
									}
								/>
								<br />
							</>
						)}
						{
							<Stack tokens={{childrenGap: 4}}>
								{checkboxBlank}
								{checkboxes}
							</Stack>
						}
						{sortedValues.length > limitCheckboxes && (
							<div style={{margin: 10}}>
								{
									<Stack horizontal horizontalAlign='space-between'>
										<div>
											<IconButton
												iconProps={{iconName: 'DoubleChevronDown'}}
												onClick={() => {
													setLimitChechboxes(
														limitCheckboxes < DEFAULT_CHECKBOX_DELTA_2
															? limitCheckboxes + DEFAULT_CHECKBOX_DELTA
															: limitCheckboxes + DEFAULT_CHECKBOX_DELTA_2,
													);
												}}
											/>
											<i>{`...${numHiddenOptions} ${t('MoreOptions')}`}</i>
										</div>
										<IconButton
											iconProps={{iconName: 'DoubleChevronUp'}}
											onClick={() => {
												refTagPicker.current?.focus();
												setLimitChechboxes(DEFAULT_CHECKBOX_NUM);
											}}
										/>
									</Stack>
								}
							</div>
						)}
					</>
				</Stack.Item>
			</Stack>
			<Separator />
		</>
	);
};

export const FilterItemDate: React.FC<FilterItemDateProps> = ({
	col,
	colValues,
	selectedFilters,
	setSelectedFilters,
}) => {
	const {name: columnName, key: columnKey} = col;

	const [isExpanded, {toggle: toggleExpanded}] = useBoolean(false);

	const {i18n} = useTranslation();

	const colValuesDateObjects = colValues
		.filter(date => date !== DEFAULT_DATE)
		.map(val => parseISO(val));

	if (colValuesDateObjects.length === 0) {
		colValuesDateObjects.push(new Date());
	}

	const earliestDate = min(colValuesDateObjects);
	const latestDate = max(colValuesDateObjects);

	const onRangeChange = (range: DateRange) => {
		const filterFn = (item: any) => {
			if (range.dateFrom && range.dateTo) {
				return isWithinInterval(parseISO(item[col.fieldName || '']), {
					start: range.dateFrom,
					end: range.dateTo,
				});
			}

			if (range.dateFrom) {
				return isAfter(parseISO(item[col.fieldName || '']), range.dateFrom);
			}

			if (range.dateTo) {
				return isBefore(parseISO(item[col.fieldName || '']), range.dateTo);
			}

			return true;
		};

		const displayDateFrom = range.dateFrom
			? formatDateTime(range.dateFrom, i18n)
			: '';
		const displayDateTo = range.dateTo
			? formatDateTime(range.dateTo, i18n)
			: '';
		const newFilters = [
			...selectedFilters.filter(f => f.columnKey !== columnKey),
			{
				columnKey,
				selection: '',
				display: `${displayDateFrom}-${displayDateTo}`,
				filterFn,
			},
		];

		setSelectedFilters(newFilters);
	};

	return (
		<>
			<Stack tokens={{childrenGap: 8}}>
				<CommandButton
					iconProps={{iconName: isExpanded ? 'ChevronUp' : 'ChevronDown'}}
					text={columnName}
					onClick={() => toggleExpanded()}
					ariaLabel={`Toggle filter ${columnName}`}
				/>

				<Stack.Item
					styles={{
						root: {
							display: isExpanded ? 'block' : 'none',
						},
					}}
				>
					<DateRangePicker
						defaultDateFrom={earliestDate}
						defaultDateTo={latestDate}
						onRangeChange={onRangeChange}
					/>
				</Stack.Item>
			</Stack>
			<Separator />
		</>
	);
};

export const DateRangePicker: React.FC<{
	onRangeChange?: (range: DateRange) => void;
	defaultDateFrom?: Date;
	defaultDateTo?: Date;
	dateFromProps?: LocalizedDatePickerProps;
	dateToProps?: LocalizedDatePickerProps;
}> = ({
	onRangeChange,
	defaultDateFrom,
	defaultDateTo,
	dateFromProps,
	dateToProps,
}) => {
	const [dateFrom, setDateFrom] = React.useState<Date | undefined>(
		defaultDateFrom,
	);
	const [dateTo, setDateTo] = React.useState<Date | undefined>(defaultDateTo);
	const [range, setRange] = React.useState({dateFrom, dateTo});

	const onDateFromChange = React.useCallback(
		(date: Date | null | undefined) => {
			if (date) {
				setDateFrom(date);
				const newRange = {...range, dateFrom: date};
				setRange(newRange);
				if (onRangeChange) {
					onRangeChange(newRange);
				}
			}
		},
		[dateFrom, range, onRangeChange],
	);
	const onDateToChange = React.useCallback(
		(date: Date | null | undefined) => {
			if (date) {
				setDateTo(date);
				const newRange = {...range, dateTo: date};
				setRange(newRange);
				if (onRangeChange) {
					onRangeChange(newRange);
				}
			}
		},
		[dateTo, range, onRangeChange],
	);

	return (
		<Stack horizontal tokens={{childrenGap: 4}}>
			<LocalizedDatePicker
				{...dateFromProps}
				label='From'
				onSelectDate={onDateFromChange}
				value={dateFrom}
				maxDate={dateTo}
			/>
			<LocalizedDatePicker
				{...dateToProps}
				label='To'
				onSelectDate={onDateToChange}
				value={dateTo}
				minDate={dateFrom}
			/>
		</Stack>
	);
};
